import { dataLayer } from '../../../data/dataLayer'
import {
  AssessmentBlueprint,
  AssessmentAttempt,
  AssessmentReport,
} from '../../../assessment/types'
import { debug } from '../../../utils/logger'

/**
 * Seeds realistic assessment data directly into the dataLayer.
 * This is separate from the MultiAccountStateV4 because assessments
 * are stored in their own domain (D3).
 */
export async function seedDemoAssessments(
  djaydenId: string,
  sophieId: string,
  teacherId?: string,
  classroomId?: string,
): Promise<void> {
  const now = Date.now()

  // ─── Djayden's Assessment (Rekenen & Getalbegrip) ─────────────────────────
  const djaydenBlueprintId = `demo_bp_${djaydenId}_rek`
  const djaydenAttemptId = `demo_att_${djaydenId}_rek`

  const djaydenBlueprint: AssessmentBlueprint = {
    id: djaydenBlueprintId,
    createdAt: now - 14 * 24 * 60 * 60 * 1000, // 2 weeks ago
    selectedGroup: 7,
    config: {
      questionCount: 15,
      moduleFilter: ['Basisrekenen', 'Getalbegrip'],
      minModules: 1,
      countsForMastery: true,
      mode: 'general',
      domainId: 'rekenen',
    },
    selectionPlan: {
      weakPercentage: 30,
      mediumPercentage: 40,
      strongPercentage: 30,
      actualDistribution: { weak: 5, medium: 6, strong: 4 },
    },
    questions: [
      {
        questionId: 'REK-CIJF-PLUS-001',
        familyId: 'REK-CIJF-PLUS',
        tier: 11,
        module: 'Basisrekenen',
        masteryCategory: 'strong',
      },
      {
        questionId: 'GET-BREUK-BASIS-001',
        familyId: 'GET-BREUK-BASIS',
        tier: 11,
        module: 'Getalbegrip',
        masteryCategory: 'strong',
      },
      // ... simplified for demo
    ],
    masterySnapshot: {
      totalFamilies: 20,
      weakFamilies: 2,
      mediumFamilies: 8,
      strongFamilies: 10,
    },
    eligibilityFilter: 'demo',
    pickedQuestionIds: ['REK-CIJF-PLUS-001', 'GET-BREUK-BASIS-001'],
  }

  const djaydenAttempt: AssessmentAttempt = {
    id: djaydenAttemptId,
    blueprintId: djaydenBlueprintId,
    startedAt: now - 14 * 24 * 60 * 60 * 1000,
    completedAt: now - 14 * 24 * 60 * 60 * 1000 + 15 * 60 * 1000, // 15 mins later
    selectedGroup: 7,
    learnerId: djaydenId,
    countsForMastery: true,
    totalTimeSeconds: 900,
    status: 'completed',
    domainId: 'rekenen',
    answers: [
      {
        questionId: 'REK-CIJF-PLUS-001',
        userChoice: 0,
        isCorrect: true,
        timeSpent: 45,
        familyId: 'REK-CIJF-PLUS',
        module: 'Basisrekenen',
      },
      {
        questionId: 'GET-BREUK-BASIS-001',
        userChoice: 1,
        isCorrect: true,
        timeSpent: 60,
        familyId: 'GET-BREUK-BASIS',
        module: 'Getalbegrip',
      },
    ],
  }

  const djaydenReport: AssessmentReport = {
    id: `demo_rep_${djaydenId}_rek`,
    attemptId: djaydenAttemptId,
    blueprintId: djaydenBlueprintId,
    generatedAt: now - 14 * 24 * 60 * 60 * 1000 + 15 * 60 * 1000,
    learnerId: djaydenId,
    selectedGroup: 7,
    summary: {
      totalQuestions: 15,
      correctCount: 13,
      accuracy: 86.7,
      totalTimeSeconds: 900,
      avgTimePerQuestion: 60,
    },
    modulePerformance: [
      {
        moduleId: 'Basisrekenen',
        totalQuestions: 8,
        correctCount: 7,
        accuracy: 87.5,
        avgTimePerQuestion: 55,
      },
      {
        moduleId: 'Getalbegrip',
        totalQuestions: 7,
        correctCount: 6,
        accuracy: 85.7,
        avgTimePerQuestion: 65,
      },
    ],
    tierPerformance: [],
    familyPerformance: [],
    recommendations: [],
  }

  // ─── Sophie's Assessment (Spelling) ───────────────────────────────────────
  const sophieBlueprintId = `demo_bp_${sophieId}_spl`
  const sophieAttemptId = `demo_att_${sophieId}_spl`

  const sophieBlueprint: AssessmentBlueprint = {
    id: sophieBlueprintId,
    createdAt: now - 5 * 24 * 60 * 60 * 1000, // 5 days ago
    selectedGroup: 8,
    config: {
      questionCount: 20,
      moduleFilter: ['SpellingWW', 'SpellingRegel'],
      minModules: 1,
      countsForMastery: true,
      mode: 'general',
      domainId: 'taalverzorging',
    },
    selectionPlan: {
      weakPercentage: 20,
      mediumPercentage: 30,
      strongPercentage: 50,
      actualDistribution: { weak: 4, medium: 6, strong: 10 },
    },
    questions: [
      {
        questionId: 'SWW-TT-STAM-001',
        familyId: 'SWW-TT-STAM',
        tier: 14,
        module: 'SpellingWW',
        masteryCategory: 'strong',
      },
    ],
    masterySnapshot: {
      totalFamilies: 25,
      weakFamilies: 1,
      mediumFamilies: 4,
      strongFamilies: 20,
    },
    eligibilityFilter: 'demo',
    pickedQuestionIds: ['SWW-TT-STAM-001'],
  }

  const sophieAttempt: AssessmentAttempt = {
    id: sophieAttemptId,
    blueprintId: sophieBlueprintId,
    startedAt: now - 5 * 24 * 60 * 60 * 1000,
    completedAt: now - 5 * 24 * 60 * 60 * 1000 + 12 * 60 * 1000, // 12 mins later
    selectedGroup: 8,
    learnerId: sophieId,
    countsForMastery: true,
    totalTimeSeconds: 720,
    status: 'completed',
    domainId: 'taalverzorging',
    answers: [
      {
        questionId: 'SWW-TT-STAM-001',
        userChoice: 2,
        isCorrect: true,
        timeSpent: 30,
        familyId: 'SWW-TT-STAM',
        module: 'SpellingWW',
      },
    ],
  }

  const sophieReport: AssessmentReport = {
    id: `demo_rep_${sophieId}_spl`,
    attemptId: sophieAttemptId,
    blueprintId: sophieBlueprintId,
    generatedAt: now - 5 * 24 * 60 * 60 * 1000 + 12 * 60 * 1000,
    learnerId: sophieId,
    selectedGroup: 8,
    summary: {
      totalQuestions: 20,
      correctCount: 19,
      accuracy: 95.0,
      totalTimeSeconds: 720,
      avgTimePerQuestion: 36,
    },
    modulePerformance: [
      {
        moduleId: 'SpellingWW',
        totalQuestions: 10,
        correctCount: 10,
        accuracy: 100,
        avgTimePerQuestion: 35,
      },
      {
        moduleId: 'SpellingRegel',
        totalQuestions: 10,
        correctCount: 9,
        accuracy: 90,
        avgTimePerQuestion: 37,
      },
    ],
    tierPerformance: [],
    familyPerformance: [],
    recommendations: [],
  }

  // Save to dataLayer
  try {
    await dataLayer.saveAssessmentBlueprint(
      djaydenBlueprintId,
      djaydenBlueprint,
    )
    await dataLayer.saveAssessmentAttempt(djaydenAttemptId, djaydenAttempt)
    await dataLayer.saveAssessmentReport(djaydenReport.id, djaydenReport)

    await dataLayer.saveAssessmentBlueprint(sophieBlueprintId, sophieBlueprint)
    await dataLayer.saveAssessmentAttempt(sophieAttemptId, sophieAttempt)
    await dataLayer.saveAssessmentReport(sophieReport.id, sophieReport)

    // ─── Teacher Assignment ("Toets Klaargezet") ──────────────────────────────
    // Seeds a "rekenen" assessment assigned to the whole groep 7 class
    // so the AssignedAssessmentBanner appears on the HomePage and
    // the "Klaargezette Toetsen" section appears on the AssessmentPage.
    if (teacherId) {
      const assignmentId = `demo_assignment_rek_g7`
      const assignment = {
        id: assignmentId,
        teacherId,
        classroomId: classroomId || 'demo_classroom',
        domainId: 'rekenen',
        domainLabel: 'Rekenen',
        group: 7,
        questionCount: 15,
        estimatedMinutes: 20,
        targetStudentIds: [],
        wholeClass: true,
        selectedFamilyIds: [
          'REK-CIJF-PLUS',
          'REK-CIJF-MIN',
          'REK-CIJF-KEER',
          'GET-BREUK-BASIS',
          'GET-BREUK-LEZEN',
        ],
        type: 'targeted' as const,
        selectedSkillSummary: 'cijferend rekenen, breuken basis',
        createdAt: now - 2 * 24 * 60 * 60 * 1000, // 2 days ago
        deadline: now + 5 * 24 * 60 * 60 * 1000, // 5 days from now
        status: 'active',
        completedByStudentIds: [],
      }
      await dataLayer.saveTeacherAssignment(assignmentId, assignment)
      debug('✅ Demo teacher assignment (toets klaargezet) seeded')
    }

    debug('✅ Demo assessment data seeded successfully')
  } catch (error) {
    debug('❌ Failed to seed demo assessment data:', error)
  }
}
